import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { z } from 'zod';

export function useLocalStorageState<T>(
  key: `ls:${string}` | null,
  defaultValue: T,
  schema?: z.ZodSchema<T>
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => {
    if (!key) return defaultValue;

    const valueInLocalStorage = window.localStorage.getItem(key);
    try {
      if (valueInLocalStorage)
        return schema
          ? schema.parse(JSON.parse(valueInLocalStorage))
          : JSON.parse(valueInLocalStorage);
    } catch {
      // pass
    }
    return defaultValue;
  });

  // Use refs to avoid unnecessary effect triggers
  const defaultValueRef = useRef(defaultValue);
  const schemaRef = useRef(schema);
  defaultValueRef.current = defaultValue;
  schemaRef.current = schema;

  // Refetch value from local storage if key changes
  useEffect(() => {
    if (!key) return;

    const valueInLocalStorage = window.localStorage.getItem(key);
    if (valueInLocalStorage) {
      try {
        const parsedValue = schemaRef.current
          ? schemaRef.current.parse(JSON.parse(valueInLocalStorage))
          : JSON.parse(valueInLocalStorage);
        setState(parsedValue);
      } catch {
        setState(defaultValueRef.current);
      }
    } else {
      setState(defaultValueRef.current);
    }
  }, [key]);

  const onChange: Dispatch<SetStateAction<T>> = useCallback(
    (newState) => {
      if (!key) {
        setState(newState);
        return;
      }
      setState((prev) => {
        const newStateValue =
          newState instanceof Function ? newState(prev) : newState;
        window.localStorage.setItem(key, JSON.stringify(newStateValue));
        return newStateValue;
      });
    },
    [key, setState]
  );

  return [state, onChange] as [T, Dispatch<SetStateAction<T>>];
}
